// logUpload.js
// Pure helpers for match-log upload: payload shaping + a localStorage
// outbox for retrying uploads that failed while offline. Mirrors the
// pattern in src/utils/arena/pendingReports.js. All side-effecting deps
// (storage, clock) are injected so the logic is unit-testable.

import { api as defaultApi } from '../../serverClient';

const QUEUE_KEY = 'valkenhall-pending-match-logs';
export const LOG_QUEUE_TTL_MS = 24 * 60 * 60 * 1000;

export function buildLogUploadPayload(log) {
  return { roomCode: log.roomCode, log };
}

export function makePendingLogQueue({ store, now = () => Date.now() } = {}) {
  const read = () => {
    try {
      const raw = store.getItem(QUEUE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const write = (entries) => store.setItem(QUEUE_KEY, JSON.stringify(entries));
  const fresh = (entries) => entries.filter((e) => now() - e.queuedAt <= LOG_QUEUE_TTL_MS);

  return {
    list() {
      return fresh(read());
    },
    save({ roomCode, log }) {
      const entries = fresh(read());
      const existing = entries.find((e) => e.roomCode === roomCode);
      const kept = entries.filter((e) => e.roomCode !== roomCode);
      // Re-queues keep the FIRST queue time. Stamping a fresh queuedAt
      // on every failed retry pushed expiry another 24h out each time,
      // so a persistently failing log never aged out of the queue.
      kept.push({ roomCode, log, queuedAt: existing?.queuedAt ?? now() });
      write(kept);
    },
    remove(roomCode) {
      write(fresh(read()).filter((e) => e.roomCode !== roomCode));
    },
  };
}

let _sharedQueue = null;
function sharedQueue() {
  if (_sharedQueue) return _sharedQueue;
  _sharedQueue = makePendingLogQueue({ store: globalThis.localStorage });
  return _sharedQueue;
}

export async function uploadMatchLog(log, { api = defaultApi, queue = sharedQueue() } = {}) {
  const { roomCode } = log;
  // Guard: never POST without a room code. It produces `/matches//log` (404)
  // and a junk queue entry. A log with no room can't be reconciled anyway.
  if (!roomCode) return { status: 'skipped_no_room' };
  try {
    const res = await api.post(`/matches/${encodeURIComponent(roomCode)}/log`, buildLogUploadPayload(log));
    queue.remove(roomCode);
    return res;
  } catch (err) {
    // A 4xx is the server saying "never": re-sending the same payload
    // can only be rejected again (too large, malformed, unauthorized).
    // Drop it. Re-queueing rejected logs retried them on every
    // reconnect forever. Network errors (no status) and 5xx are
    // plausibly transient, so those stay queued until the TTL.
    if (typeof err?.status === 'number' && err.status >= 400 && err.status < 500) {
      queue.remove(roomCode);
      return { status: 'rejected', code: err.status };
    }
    queue.save({ roomCode, log });
    return { status: 'queued' };
  }
}

export async function drainPendingLogs({ api = defaultApi, queue = sharedQueue() } = {}) {
  for (const entry of queue.list()) {
    await uploadMatchLog(entry.log, { api, queue });
  }
}
